<template>
  <div
    class="search-page"
    data-test="search-page">
    <div
      v-show="!load_more_episodes && !see_all_shows"
      class="container">
      <search-bar
        :query="query"
        data-test="searchbar"
        @onSearch="onSearch"
        @clearSearch="clearInput"/>
      <el-alert
        v-show="data_searched && !searching && !publishers.length && !episodes.length && !podcasts.length"
        :closable="false"
        title="No Results Found!"
        type="warning"
        center
        show-icon/>
    </div>
    <!-- Presearch -->
    <template v-if="!searching && !query">
      <loader
        v-show="!data_loaded"
        :center="true"
        label="Loading Presearch Data.."/>
      <div v-show="data_loaded">
        <div
          v-if="search_result && search_result.trending_searches"
          class="container trending">
          <h4>Trending Searches</h4>
          <el-row
            v-check-visible-viewport="onTrendingSearchesVisible"
            :gutter="24"
            class="t-s-l">
            <el-col
              v-for="(item,index) in search_result.trending_searches.slice(0,8)"
              :sm="12"
              :xs="24"
              :key="index"
              data-test="list-trendingsearch">
              <search-list
                :text="item"
                :link="'/listen/search?q='+ item"
                :class="{'s-b-1': index === 0 || index === 1}"
                type="trending-search"
                data-test="trending-search-option"
                @click.native="suggestedClick(item)"/>
            </el-col>
          </el-row>
        </div>
        <div class="clear"/>
        <div
          v-if="search_result && search_result.top_categories"
          class="container categories">
          <h4>Top Categories</h4>
          <el-row
            v-check-visible-viewport="onTopCategoriesVisible"
            :gutter="24"
          >
            <el-col
              v-for="(item,index) in search_result.top_categories.slice(0,8)"
              :sm="12"
              :xs="24"
              :key="index"
              data-test="list-topcategories">
              <search-list
                :text="item.name"
                :link="`/listen/category/${item.slug}/${item.uuid}`"
                :class="{'s-b-1': index === 0 || index === 1}"
                type="top-category"
                data-test="top-categories-option"/>
            </el-col>
          </el-row>
        </div>
        <el-alert
          v-if="!search_result || (!search_result.suggested && !search_result.trending && !search_result.top_categories)"
          :closable="false"
          class="container"
          title="No presearch data found"
          type="warning"
          show-icon
          center/>
      </div>
    </template>
    <!-- Search Data -->
    <div
      v-show="!see_all_shows"
      class="search-data"
      data-test="search-data">
      <loader
        v-show="searching"
        :center="true"
        label="Searching.."/>
      <!-- Display Podcasts -->
      <div
        v-if="data_searched && !searching"
        v-show="!load_more_episodes"
        class="podcast-section"
        data-test="podcast-section">
        <podcast-list
          v-if="podcasts.length && !searching"
          :see_all="true"
          :podcast_offset="podcast_limit"
          :key="0"
          :bucket = "{
            bucket_uuid: null,
            name: 'Shows',
            bucket_type:'search_podcasts',
            items_count: podcasts.length,
            media_size: 'thumbnail',
            items: podcasts,
            screen_name: 'search_results',
          }"
          @moreClicked="see_all_shows = true"/>
      </div>
      <!-- Display Episodes -->
      <div
        v-if="data_searched && !searching"
        class="episode-section container"
        data-test="section-episode">
        <div
          v-check-visible-viewport="onEpisodesListVisible"
          v-show="!load_more_episodes">
          <el-row
            v-show="!load_more_episodes && episodes.length"
            type="flex"
            align="middle"
            justify="space-between"
            class="header">
            <h4>Episodes {{ Number(episodes_hit).toLocaleString() }}</h4>
            <p
              v-button-event="{buttonText: 'see all episodes', buttonLink: 'search_results_episodes'}"
              data-test="see-all-episodes"
              @click="loadMoreEpisodes">
              SEE ALL
              <i class="el-icon-caret-right el-icon-right" />
            </p>
          </el-row>
          <card-list
            v-for="(item,index) in episodes"
            :key="index"
            :is-exclusive="item.is_exclusive || item.podcast_is_exclusive"
            :is-free="!item.is_exclusive && item.podcast_is_exclusive"
            :season_no="item.season_number_formatted || 0"
            :episode_no="item.episode_number_formatted || 0"
            :shows="item"
            :podcast="item.season.podcast"
            :bucket = "episodesBucket"
          />
        </div>
        <div
          v-if="load_more_episodes">
          <el-row
            v-show="load_more_episodes"
            type="flex"
            align="middle"
            class="back-result">
            <el-col :span="24">
              <span
                class="back"
                data-test="back"
                @click="backToResults"> <i class="fa fa-angle-left" /> BACK TO RESULTS FOR {{ `"${query}"` }}
              </span>
            </el-col>
          </el-row>
          <card-list-wrapper
            :episodes="episodes"
            :bucket="{
              bucket_uuid: null,
              name: 'Episodes',
              position: 1,
              bucket_type: 'see_all_episodes',
              media_size: 'episode_details',
              screen_name: 'search_results_episodes',
            }"
          />
          <loader
            v-show="loading_more_episodes"
            :with-margin="true"/>
        </div>
      </div>
      <!-- Display Publishers -->
      <div
        v-show="!load_more_episodes"
        v-if="publishers.length && !searching"
        class="container publishers-section"
        data-test="section-publisher">
        <h4 class="publisher-title">Publishers</h4>
        <el-row
          v-check-visible-viewport="onPublishersListVisible"
          :gutter="24"
        >
          <el-col
            v-for="(publisher, index) in publishers"
            :sm="12"
            :xs="24"
            :key="index"
            data-test="list-publisher">
            <search-list
              :item-data="publisher"
              :key="index"
              :text="publisher.name"
              :link="`/listen/${publisher.slug}/${publisher.uuid}?page=1`"
              type="publisher"
              data-test="search-publisher"/>
          </el-col>
        </el-row>
      </div>
    </div>
    <div
      v-if="see_all_shows && podcasts.length"
      class="more-shows container"
      data-test="more-shows">
      <el-row
        type="flex"
        align="middle"
        class="back-result">
        <el-col :span="24">
          <span
            data-test="back"
            class="back"
            @click="see_all_shows = false"> <i class="fa fa-angle-left" /> BACK TO RESULTS FOR {{ `"${query}"` }}
          </span>
        </el-col>
      </el-row>
      <podcast-grid
        v-if="see_all_shows && podcasts.length"
        :list="podcasts"
        :bucket="{
          bucket_uuid: null,
          position: 0,
          name: 'Shows',
          bucket_type: 'see_all_podcasts',
          media_size: 'thumbnail',
          items_count: podcasts.length,
          screen_name: 'search_results_shows',
        }"
        type="podcast"/>
      <loader
        v-show="loading_more_podcasts"
        :with-margin="true"/>
    </div>
  </div>
</template>
<script>
import * as log from 'loglevel';
import SearchBar from '../components/SearchBar';
import PodcastList from '../components/PodcastList';
import CategoryList from '../components/CategoryList';
import Search from '../services/Search';
import PodcastService from '../services/Podcast';
import Loader from '../components/Loader';
import PodcastCard from '../components/PodcastCard';
import CategoryCard from '../components/CategoryCard';
import CardList from '../components/CardList';
import CardListWrapper from '../components/CardListWrapper';
import SearchListing from '../components/SearchListing';
import PodcastGrid from '../components/PodcastGrid';
import SegmentEvent from '../services/PlayerEvent';
import pageMixin from '../shared/pageMixin';

export default {
  name: 'Search',
  components: {
    'search-bar': SearchBar,
    'search-list': SearchListing,
    'podcast-list': PodcastList,
    'category-list': CategoryList,
    loader: Loader,
    'podcast-card': PodcastCard,
    'category-card': CategoryCard,
    'card-list': CardList,
    'card-list-wrapper': CardListWrapper,
    'podcast-grid': PodcastGrid,
  },
  mixins: [pageMixin],
  data() {
    return {
      pageName: 'Presearch',
      search_result: [],
      isLoggedIn: this.$store.getters.loggedIn,
      searchProvider: new Search(),
      podcast_service: new PodcastService(),
      query: '',
      more_episodes: false,
      scroll_threshold: 500,
      suggestedItems: [],
      trending: [],
      top_categories: [],
      episodes: [],
      podcasts: [],
      publishers: [],
      episodes_response: [],

      load_more_episodes: false,
      data_loaded: false,
      searching: false,
      data_searched: false,
      loading_more_episodes: false,
      loading_more_podcasts: false,
      loading_more_publishers: false,
      see_all_shows: false,

      slide_podcast_right_counter: 0,
      slide_publisher_right_counter: 0,

      episode_offset: 0,
      episode_limit: 20,
      podcast_offset: 0,
      podcast_limit: 20,
      publisher_offset: 0,
      publisher_limit: 20,

      episodes_hit: 0,
      podcasts_hit: 0,
      publishers_hit: 0,
      isEpisodeScrolling: null,
      episodesBucket: {},
      publishersBucket: {},
      episodeViewed: [],
      eventProvider: new SegmentEvent(),
    };
  },
  watch: {
    see_all_shows() {
      if (this.see_all_shows) this.infiniteScrollPodcasts();
      else this.removePodcastInfiniteScrollEvent();
    },
    load_more_episodes() {
      if (!this.load_more_episodes) this.removeEpisodeInfiniteScrollEvent();
    },
  },
  mounted() {
    this.initBuckets();
    const query = this.$route.query.q;
    if (query && query.length) {
      this.onSearch(query);
    } else {
      this.loadPresearch();
    }
  },
  beforeDestroy() {
    this.removeEpisodeInfiniteScrollEvent();
    this.removePodcastInfiniteScrollEvent();
  },
  methods: {
    initBuckets() {
      this.episodesBucket = {
        bucket_uuid: null,
        name: 'Episodes',
        position: 1,
        bucket_type: 'see_all_episodes',
        media_size: 'episode_details',
        screen_name: 'search_results',
      };
    },
    // Infinite Scroll Episodes
    infiniteScrollEpisodes() {
      this.addOverflowY();
      document.getElementsByTagName('body')[0].addEventListener('scroll', this.infiniteScrollEpisodesCallback);
    },
    infiniteScrollEpisodesCallback() {
      const heightLimit = document.body.scrollTop + window.innerHeight + this.scroll_threshold >= document.body.scrollHeight;
      if (heightLimit && !this.loading_more_episodes) {
        this.loadMoreEpisodes();
      }
    },
    removeEpisodeInfiniteScrollEvent() {
      this.removeOverflowY();
      document.getElementsByTagName('body')[0].removeEventListener('scroll', this.infiniteScrollEpisodesCallback);
    },
    // Infinite Scroll Podcasts
    infiniteScrollPodcasts() {
      this.addOverflowY();
      document.getElementsByTagName('body')[0].addEventListener('scroll', this.infiniteScrollPodcastsCallBack);
    },
    infiniteScrollPodcastsCallBack() {
      const heightLimit = document.body.scrollTop + window.innerHeight + this.scroll_threshold >= document.body.scrollHeight;
      if (heightLimit && !this.loading_more_podcasts) {
        this.loadMorePodcasts();
      }
    },
    removePodcastInfiniteScrollEvent() {
      this.removeOverflowY();
      document.getElementsByTagName('body')[0].removeEventListener('scroll', this.infiniteScrollPodcastsCallBack);
    },
    addOverflowY() {
      const body = document.getElementsByTagName('body')[0];
      body.style.overflow = 'auto';
    },
    removeOverflowY() {
      const body = document.getElementsByTagName('body')[0];
      body.style = {};
    },
    clearInput() {
      this.query = '';
      this.data_searched = false;
      this.load_more_episodes = false;

      this.podcasts = [];
      this.episodes = [];
      this.publishers = [];

      this.$router.push({ query: null });
    },
    backToResults() {
      this.episodes = this.episodes.length ? this.episodes.slice(0, 5) : [];
      this.load_more_episodes = false;
    },
    handleSelect(query) {
      this.query = query;
      this.onSearch();
    },
    onSearch(query) {
      this.query = query;
      this.load_more_episodes = false;
      this.podcasts = [];
      this.episodes = [];
      this.publishers = [];
      this.$router.push({ query: { q: query } });
      this.searchData();
    },
    loadPresearch() {
      this.search_result = this.$store.getters.presearch;
      if (this.search_result && this.search_result.length) {
        this.data_loaded = true;
        return;
      }
      this.searchProvider.loadPresearch()
        .then((res) => {
          this.search_result = res || [];
          this.$store.dispatch('setPresearch', this.search_result);
        })
        .catch((err) => {
          log.info(err.message);
        })
        .then(() => {
          this.data_loaded = true;
        });
    },
    loadMoreEpisodes() {
      if (this.loading_more_episodes) return;
      if (!this.load_more_episodes) this.infiniteScrollEpisodes();
      this.loading_more_episodes = true;
      this.load_more_episodes = true;
      this.episode_offset += this.episode_limit;
      this.searchProvider.searchData(this.query, this.episode_offset, this.episode_limit, 'episodes')
        .then((res) => {
          this.episodes = this.episodes.concat(this.episodes_response.slice(5, 20)).concat(res.episodes || []);
          this.episodes = this.superImposeEpisodes();
        })
        .catch((err) => { log.warn(err.message); })
        .then(() => {
          this.loading_more_episodes = false;
        });
    },
    loadMorePodcasts() {
      if (this.loading_more_podcasts || this.podcast_offset >= this.podcasts_hit) return;
      this.loading_more_podcasts = true;
      this.podcast_offset += this.podcast_limit;
      this.searchProvider.searchData(this.query, this.podcast_offset, this.podcast_limit, 'podcasts')
        .then((res) => {
          this.podcasts = this.podcasts.concat(res.podcasts || []);
          this.podcasts = this.superImposePodcasts();
        })
        .catch(err => log.warn(err.message))
        .then(() => { this.loading_more_podcasts = false; });
    },
    loadMorePublishers() {
      if (this.loading_more_publishers) return;
      this.publisher_offset += this.publisher_limit;
      this.loading_more_publishers = true;
      this.searchProvider.searchData(this.query, this.publisher_offset, this.publisher_limit, 'publishers')
        .then((res) => {
          this.publishers = this.publishers.concat(res.publishers || []);
        })
        .catch(err => log.warn(err.message))
        .then(() => { this.loading_more_publishers = false; });
    },
    superImposeEpisodes() {
      return this.episodes.map((episode) => {
        const keys = Object.keys(episode);

        const publisherKeys = keys.filter(key => key.includes('publisher_'));
        const podcastKeys = keys.filter(key => key.includes('podcast_'));

        if (podcastKeys.length) {
          episode = {
            ...episode,
            season: {
              podcast: {},
            },
          };

          podcastKeys.forEach((key) => {
            episode.season.podcast[key.split('podcast_')[1]] = episode[key];
          });

          if (publisherKeys.length) {
            episode.season.podcast.publisher = {};

            publisherKeys.forEach((key) => {
              episode.season.podcast.publisher[key.split('publisher_')[1]] = episode[key];
            });
          }
        }

        return episode;
      });
    },
    superImposePodcasts() {
      return this.podcasts.map((podcast) => {
        const keys = Object.keys(podcast);
        const filteredKeys = keys.filter(key => key.includes('publisher_'));

        if (filteredKeys.length) {
          podcast = {
            ...podcast,
            publisher: {},
          };

          filteredKeys.forEach((key) => {
            podcast.publisher[key.split('publisher_')[1]] = podcast[key];
          });
        }

        return podcast;
      });
    },
    searchData() {
      this.searching = true;
      this.data_searched = false;

      this.searchProvider
        .searchData(this.query, 0, 20, null)
        .then((res) => {
          this.podcasts = res.podcasts || [];

          this.podcasts = this.superImposePodcasts();

          this.episodes_response = res.episodes || [];
          this.episodes = Array.isArray(res.episodes) ? res.episodes.slice(0, 5) : [];

          this.episodes = this.superImposeEpisodes();

          this.publishers = res.publishers || [];
          this.publishers = this.publishers.map(item => ({
            ...item,
            title: item.name,
          }));
          this.episodes_hit = res.episode_hits;
          this.podcasts_hit = res.podcast_hits;
          this.publishers_hit = res.publisher_hits;

          this.searching = false;
          this.data_searched = true;
        })
        .catch((err) => {
          this.searching = false;
          log.warn(err.message);

          this.showErrorMsg('Luminary is experiencing issues running this search. Try again later.');
        });
    },
    suggestedClick(str) {
      this.onSearch(str);
      if (this.isLoggedIn) {
        this.saveSuggestedSearch(str);
      }
    },
    saveSuggestedSearch(text) {
      const arr = this.$store.getters.search_data;
      if (arr.findIndex(item => item === text) === -1) {
        arr.unshift(text);
      }
      this.$store.dispatch('setRecentSearches', arr);
      this.searchProvider.saveRecentSearch(text)
        .then(() => { log.info('Search query saved'); })
        .catch((err) => { log.warn(err.message); });
    },
    onTrendingSearchesVisible() {
      const bucket = {
        name: 'Trending Searches',
        bucket_uuid: null,
        bucket_type: 'trending_searches',
        media_size: 'text',
        position: 0,
        items_count: this.search_result.trending_searches.length,
      };
      const bucketItemsSeen = this.search_result.trending_searches;
      this.eventProvider.triggerContentViewedEvent(bucket, bucketItemsSeen);
    },
    onTopCategoriesVisible() {
      const bucket = {
        name: 'Top Categories',
        bucket_uuid: null,
        bucket_type: 'top_categories',
        media_size: 'text',
        position: 1,
        items_count: this.search_result.top_categories.length,
      };
      const bucketItems = [...this.search_result.top_categories];
      const bucketItemsSeen = bucketItems.map(item => item.uuid);
      this.eventProvider.triggerContentViewedEvent(bucket, bucketItemsSeen);
    },
    onEpisodesListVisible() {
      const bucket = {
        name: 'Episodes',
        bucket_uuid: null,
        bucket_type: 'search_episodes',
        media_size: 'episode_details',
        position: 1,
        items_count: this.episodes.length,
      };
      const bucketItems = [...this.episodes];
      const bucketItemsSeen = bucketItems.map(item => item.uuid);
      this.eventProvider.triggerContentViewedEvent(bucket, bucketItemsSeen);
    },
    onPublishersListVisible() {
      const bucket = {
        name: 'Publishers',
        bucket_uuid: null,
        bucket_type: 'search_publishers',
        media_size: 'text',
        position: 2,
        items_count: this.publishers.length,
      };
      const bucketItems = [...this.publishers];
      const bucketItemsSeen = bucketItems.map(item => item.uuid);
      this.eventProvider.triggerContentViewedEvent(bucket, bucketItemsSeen);
    },
  },
};
</script>

<style lang="scss">
@import "../assets/scss/variables.scss";
.search-page {
  flex: 1;
  padding-bottom: 100px;

  .suggested-search , .trending, .categories  {
    h4 {
      margin: 56px 0 24px 0 !important;
      // padding-bottom: 24px;
      // border-bottom: 1px solid $light-gray2;
      font-weight: 500 !important;
    }
  }
  .s-b-1 {
    border-top:1px solid $light-gray2;
  }
  .suggested-search {
    margin-top: 40px;
    h4 {
      margin-bottom: 16px;
    }
    .suggested-search-item .el-button {
      margin-right: 8px;
      margin-bottom: 8px;
    }
  }
  .list-header {
    margin-top: 40px;
  }
  .search-data {
    .podcast-section, .episode-section, .publishers-section {
      &:not(.publishers-section) {
        margin-bottom: 56px;
      }
      .header {
        margin-bottom: 32px;
        h4 {
          margin: 0 0;
        }
        p {
          font-size: 13px;
          font-family: Favorit-light;
          font-weight: 600;
          color: $blue;
          cursor: pointer;
        }
      }
      .publisher-title {
        margin-bottom: 20px !important;
      }
    }
    .podcast-section {
      .podcast-card {
        height: 300px !important;
      }
      .podcast-content {
        margin-top: 10px;
      }
    }
  }
  .el-alert {
    margin-top:75px;
  }
  .back-result {
    padding: 2rem 0;
    .back {
      text-transform: uppercase;
      font-size: 0.85rem;
      color: $light-gray;
      font-weight: 700;
      cursor: pointer;
      i {
        font-size: 1rem;
        color: $light-gray;
        font-weight: 800;
        margin-right: 1rem;
      }
    }
  }
  .episode-section {
    .back {
      margin: 0 15px;
    }
  }
  .publishers-section {
    .section-title {
      margin-bottom: 16px;
    }
    .publisher {
      margin-bottom: 8px;
      margin-left: 0;
      margin-right: 8px;
    }
  }
  .more-shows {
    .list-header {
      display: none;
    }
  }
}
@media (max-width: 375px) {
  .episode-section {
    .header {
      margin: 0 15px !important;
    }
  }
}
@media (max-width: 576px) {
  .search-page {
    padding-bottom: 50px;
    .search-bar {
      margin: 32px auto;
    }

    .suggested-search {
      margin-top: 30px;
      .el-button {
        padding: 8px 16px;
        font-size: 14px;
      }
    }
    .header {
      margin: 0 30px 20px 32px;
    }
    .list-header {
      margin-top: 30px;
    }
    .publishers-section {
      .el-button {
        padding: 12px 20px;
      }
    }

    .search-data {
      .podcast-section, .episode-section, .publishers-section {
        margin-top: 35px;
      }
    }

    .show-card-list {
      padding-top: 16px;
      padding-bottom: 20px;
    }
  }
}
@media (max-width: 767px) {
  .trending > div > div:nth-child(2) >div {
    border-top: 0 !important;
  }
  .categories > div > div:nth-child(2) >div {
    border-top: 0 !important;
  }
}
@media (max-width: 1024px) {
  .search-data {
    .header {
      margin: 0 36px;
    }
    .episode-section {
      width: 100% !important;
    }
  }
}
</style>



// WEBPACK FOOTER //
// app/src/pages/Search.vue